// @file Surface sections store
// This is old grouping logic that only support group by section.
// Only use this store if 'groupPostsV2' flip is not enabled, else use surface_post_group_by_store.ts.
import type { SectionActionHistoryData } from '@@/bits/action_history_manager'
import { ActionHistoryType, recordActionHistory } from '@@/bits/action_history_manager'
import { trackEvent } from '@@/bits/analytics'
import { ALERT_ICON } from '@@/bits/confirmation_dialog'
import device from '@@/bits/device'
import { captureException, captureFetchException } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { MAX_SECTION_TITLE_LENGTH, MIN_SECTION_TITLE_LENGTH } from '@@/bits/numbers'
import { getVuexStore } from '@@/bits/pinia'
import { isPostPinned } from '@@/bits/post_state'
import sectionSorter from '@@/bits/section_sorter'
import { vDel } from '@@/bits/vue'
import { useGlobalAlertDialogStore } from '@@/pinia//global_alert_dialog'
import {
  OzConfirmationDialogBoxButtonScheme,
  useGlobalConfirmationDialogStore,
} from '@@/pinia/global_confirmation_dialog'
import { SnackbarNotificationType, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useNativeAppStore } from '@@/pinia/native_app'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfaceGroupPickerStore } from '@@/pinia/surface_group_picker'
import { useSurfaceMapStore } from '@@/pinia/surface_map'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceShareLinksStore } from '@@/pinia/surface_share_links'
import { useSurfaceTranslationsStore } from '@@/pinia/surface_translations_store'
import { WallApi } from '@@/surface/api/wall'
import type { DeleteSectionOptions, Id, Post, Section } from '@@/types'
import { useUndoable } from '@@/vuecomposables/undoable'
import type { RootState } from '@@/vuexstore/surface/types'
import type { JsonAPIResource, JsonAPIResponse } from '@padlet/arvo'
import { isEmpty } from 'es-toolkit/compat'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

const isSectionNew = (section: Section): boolean => {
  return String(section.id).startsWith('new_')
}

export const useSurfaceSectionsStore = defineStore('surfaceSections', () => {
  const surfaceVuexStore = getVuexStore<RootState>() // For gradual conversion to pinia
  const globalAlertDialogStore = useGlobalAlertDialogStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const nativeAppStore = useNativeAppStore()
  const surfaceContainerSizeStore = useSurfaceContainerSizeStore()
  const surfaceStore = useSurfaceStore()
  const surfaceMapStore = useSurfaceMapStore()
  const surfacePostsStore = useSurfacePostsStore()
  const surfaceShareLinksStore = useSurfaceShareLinksStore()
  const surfaceDemoPadletPanelStore = useSurfaceOnboardingDemoPadletPanelStore()
  const surfaceTranslationsStore = useSurfaceTranslationsStore()
  const confirmationDialogStore = useGlobalConfirmationDialogStore()
  const { extractSectionDataForUndoRedo, setDeletedSnackbar } = useUndoable()

  /*
   * STATE
   */
  const sectionEntitiesRaw = ref<Record<Id, Section>>({})
  const sectionEntities = computed<Record<Id, Section>>(() => {
    // Return unmodified sections if not viewing translations or translated data is missing
    if (
      !surfaceTranslationsStore.isViewingTranslatedPadlet ||
      surfaceTranslationsStore.translatedWall?.sections == null
    ) {
      return sectionEntitiesRaw.value
    }

    // Create a map of translated section titles for efficient lookup
    const translatedTitles: Record<Id, string> = surfaceTranslationsStore.translatedWall.sections.reduce<
      Record<Id, string>
    >((map, translatedSection) => {
      map[translatedSection.sectionId] = translatedSection.sectionTitle
      return map
    }, {})

    // Iterate over raw sections and merge translated titles if available
    return Object.entries(sectionEntitiesRaw.value).reduce<Record<Id, Section>>((acc, [sectionId, section]) => {
      const translatedTitle = translatedTitles[sectionId]
      acc[sectionId] = {
        ...section,
        // Only override title if a translation exists
        ...(translatedTitle !== undefined && { title: translatedTitle }),
      }
      return acc
    }, {})
  })
  const areSectionsFetched = ref(false)
  const defaultSectionId = ref<Id>()
  const sectionRecentlyCreatedId = ref<Id | null>(null)
  const sectionBeingRenamedId = ref<Id | null>(null)
  const mostRecentlyTouchedSectionId = ref<Id | null>(null)
  const sectionUnderActionId = ref<Id | null>(null)
  const sectionUnderActionRightClickCoordinates = ref<{ x: number; y: number } | null>(null)

  /*
   * GETTERS
   */
  const sectionUnderAction = computed(() => getSectionById(sectionUnderActionId.value))
  const sectionIds = computed<Id[]>(() => Object.keys(sectionEntities.value).map((id) => Number(id)))
  const rawSortedSections = computed<Section[]>(() => {
    if (surfaceShareLinksStore.isSectionBreakout && surfaceShareLinksStore.sectionBreakoutSection != null) {
      // Cannot return [surfaceShareLinksStore.sectionBreakoutSection] directly due to circular dependency!
      // surface_sections -> surface_share_links.sectionBreakoutSection -> surface_sections.getSectionById
      // This would cause TypeScript inference to fail and fallback to 'any' type
      const breakoutSection = getRawSectionById(surfaceShareLinksStore.sectionBreakoutSection.id)
      return breakoutSection == null ? [] : [breakoutSection]
    }
    return sectionSorter.sortSections(Object.values(sectionEntitiesRaw.value))
  })
  const sortedSections = computed<Section[]>(() => {
    if (surfaceShareLinksStore.isSectionBreakout && surfaceShareLinksStore.sectionBreakoutSection != null) {
      const breakoutSection = getSectionById(surfaceShareLinksStore.sectionBreakoutSection.id)
      return breakoutSection == null ? [] : [breakoutSection]
    }
    return sectionSorter.sortSections(Object.values(sectionEntities.value))
  })
  const sortedSectionIds = computed(() => {
    return sortedSections.value.filter((s) => s.id != null).map((s) => s.id)
  })
  const usableSections = computed(() => Object.values(sectionEntities.value).filter((s) => !isSectionNew(s)))
  const numOfSections = computed(() => sectionIds.value.length)
  const hasFetchedSections = computed<boolean>(() => (surfaceStore.canUseSections ? areSectionsFetched.value : true))

  const wallHasSections = computed(
    () => !isEmpty(sortedSectionIds.value) && surfaceStore.isSectioned && surfaceStore.canUseSections,
  )

  const assignedWallSectionId = computed<Id | null>(() => surfaceVuexStore?.getters['post/assignedWallSectionId'])

  /*
   * RENAME SECTION
   */
  // https://whimsical.com/sections-0-1-stream-UwXiARuawScRnEGKF2bScC@2bsEvpTYFZsxPP7WckcQqNHxJtrkdXQAyBZ
  const xSectionSpecificButtons = computed<boolean>(() => {
    if (surfaceStore.isShelf) return false
    if (surfaceStore.isMap) return surfaceMapStore.postListPosition === 'side'
    if (device.touchable) return !surfaceContainerSizeStore.isContainerSmallerThanDesktop
    return !surfaceContainerSizeStore.isContainerSmallerThanTabletPortrait
  })

  const allowOpenSectionActionMenu = computed<boolean>(() => {
    // grid has no section specific buttons so handle accordlingly
    if (surfaceStore.isGrid && surfaceContainerSizeStore.isContainerSmallerThanTabletPortrait) return true
    if (surfaceStore.isGrid && !surfaceContainerSizeStore.isContainerSmallerThanTabletPortrait) return false
    return device.touchable || !xSectionSpecificButtons.value
  })

  const canEditSectionNameInline = computed<boolean>(() => {
    if (surfaceShareLinksStore.isSectionBreakout) return false
    return (
      (!allowOpenSectionActionMenu.value || surfaceStore.isShelf) &&
      !surfaceDemoPadletPanelStore.isDemoPadletPanelDesktop &&
      !surfaceContainerSizeStore.isContainerSmallerThanTabletLandscape
    )
  })

  const xSectionRenameModal = computed<boolean>(
    () => sectionBeingRenamedId.value != null && !canEditSectionNameInline.value,
  )

  const isSectionBeingEdited = computed<boolean>(() => sectionBeingRenamedId.value != null)

  /*
   * GETTER FUNCTIONS
   */
  function getSectionById(sectionId: Id | null): Section | null {
    if (sectionId == null) return null
    return sectionEntities.value[sectionId]
  }

  function getRawSectionById(sectionId: Id | null): Section | null {
    if (sectionId == null) return null
    return sectionEntitiesRaw.value[sectionId] ?? null
  }

  function isSectionEmpty(sectionId: Id): boolean {
    const postsInSection = surfacePostsStore.postsBySectionId[sectionId]
    return postsInSection == null || postsInSection.length === 0
  }

  function hasPinnedPosts(sectionId: Id): boolean {
    const postsInSection = surfacePostsStore.postsBySectionId[sectionId] ?? []
    return postsInSection.some(isPostPinned)
  }

  function hasUnpinnedPosts(sectionId: Id): boolean {
    const postsInSection = surfacePostsStore.postsBySectionId[sectionId] ?? []
    return postsInSection.some((p) => !isPostPinned(p))
  }

  /*
   * STATE ACTIONS
   */
  function hideSectionActionMenu(): void {
    sectionUnderActionId.value = null
    sectionUnderActionRightClickCoordinates.value = null
  }

  function updateSectionEntities(json: JsonAPIResponse<Section>): void {
    const sections = (json.data as Array<JsonAPIResource<Section>>).map((d) => d.attributes)
    sectionEntitiesRaw.value = sections.reduce<Record<Id, Section>>((acc, section) => {
      acc[section.id] = section
      return acc
    }, {})
    areSectionsFetched.value = true
    void nativeAppStore.postSurfaceState()
  }

  async function fetchSections(): Promise<void> {
    try {
      const { WallSectionApi } = await import('@@/surface/api/wall_section')
      const json: JsonAPIResponse<Section> = await WallSectionApi.readAll({ wall: { id: surfaceStore.wallId } })
      updateSectionEntities(json)
    } catch (e) {
      captureFetchException(e, { source: 'fetchSections' })
    }
  }

  function insertSection(section: Section): void {
    if (section.id == null) return
    const existingSection = sectionEntitiesRaw.value[section.id]
    if (existingSection != null) return // don't insert if already exists

    sectionEntitiesRaw.value = {
      ...sectionEntitiesRaw.value,
      [section.id]: section,
    }

    if (surfaceTranslationsStore.isViewingTranslatedPadlet) {
      void surfaceTranslationsStore.translateSectionEntity(section.id)
    }
  }

  function updateSection(section: Section): void {
    if (section.id == null) return

    sectionEntitiesRaw.value = {
      ...sectionEntitiesRaw.value,
      [section.id]: section,
    }
  }

  function removeSection(section: Section): void {
    vDel(sectionEntitiesRaw.value, section.id)
    if (surfaceTranslationsStore.isViewingTranslatedPadlet) {
      void surfaceTranslationsStore.deleteSectionFromTranslatedWall(section.id)
    }
  }

  /*
   * CREATE ACTIONS
   */
  /**
   * Create a new section and with the given title
   * @param title Name of section
   * @param sortIndex If not provided, the new section will be created at the bottom of the wall.
   */
  async function createSectionWithName(
    title: string | null,
    options?: {
      promptRenameSection?: boolean
      sortIndex?: number
      gapSize?: number
      color?: string
    },
  ): Promise<Section | null> {
    try {
      const defaultSectionTitle = __('Section %{number}', {
        number: numOfSections.value + 1,
      })

      const { WallSectionApi } = await import('@@/surface/api/wall_section')
      const sectionData = await WallSectionApi.create({
        wall_id: surfaceVuexStore?.getters.wallId,
        title: title ?? defaultSectionTitle,
        sort_index: options?.sortIndex ?? sectionSorter.getBottomSortIndex(),
        gap_size: options?.gapSize ?? null,
        color: options?.color ?? null,
      })

      const section = (sectionData.data as JsonAPIResource<Section>).attributes
      insertSection(section)
      sectionRecentlyCreatedId.value = section.id ?? null

      if (options?.promptRenameSection === true) sectionBeingRenamedId.value = section.id ?? null

      updateMostRecentlyTouchedSection(section.id)
      void useNativeAppStore().postSurfaceState()

      void surfaceShareLinksStore.fetchSectionBreakoutLinks()

      return section
    } catch (e) {
      useGlobalSnackbarStore().setSnackbar({
        notificationType: SnackbarNotificationType.error,
        message: __('Failed to create section'),
      })
      captureException(e)
      return null
    }
  }

  /**
   * Create a new section and give it a default name: `Section 1`, `Section 2`,... `Section n`
   * where `n` is the number of sections in the wall.
   * @param sortIndex If not provided, the new section will be created at the bottom of the wall.
   */
  async function createSectionWithDefaultName(options?: {
    promptRenameSection?: boolean
    sortIndex?: number
    gapSize?: number
  }): Promise<void> {
    await createSectionWithName(null, options)
  }

  function createSectionBefore(sectionId: Id): void {
    const section = document.querySelector(`#section-${sectionId}`)
    if (section == null) return
    const { sortIndex, gapSize } = sectionSorter.getNewIndexBefore(section as HTMLElement)
    const createSectionOptions = {
      sortIndex,
      gapSize,
      promptRenameSection: true,
    }
    void createSectionWithDefaultName(createSectionOptions)
  }

  function createSectionAfter(sectionId: Id): void {
    const section = document.querySelector(`#section-${sectionId}`)
    if (section == null) return
    const { sortIndex, gapSize } = sectionSorter.getNewIndexAfter(section as HTMLElement)
    const createSectionOptions = {
      sortIndex,
      gapSize,
      promptRenameSection: true,
    }
    void createSectionWithDefaultName(createSectionOptions)
  }

  /*
   * DEFAULT SECTION ACTIONS
   */

  function updateMostRecentlyTouchedSection(sectionId: Id): void {
    mostRecentlyTouchedSectionId.value = sectionId
  }

  function setDefaultSectionAndRecentlyTouchedSection(sectionId: Id): void {
    defaultSectionId.value = sectionId
    updateMostRecentlyTouchedSection(sectionId)
  }

  async function changeDefaultSection(sectionId: Id): Promise<void> {
    try {
      await WallApi.update({
        ...surfaceVuexStore?.state.wall,
        wish_arrangement: {
          ...surfaceVuexStore?.state.wall.wish_arrangement,
          default_section_id: sectionId,
        },
      })

      setDefaultSectionAndRecentlyTouchedSection(sectionId)

      globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.success,
        message: __('Default section changed'),
      })
    } catch (e) {
      globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.error,
        message: __('Failed to update default section'),
      })
      captureException(e)
    }
  }

  /*
   * EDIT ACTIONS
   */

  function editSectionTitle(payload: { sectionId: Id; title: string }): boolean {
    const { sectionId, title } = payload
    const trimmedTitle = title.trim()

    // Validation
    const isTitleTooShort = trimmedTitle.length < MIN_SECTION_TITLE_LENGTH
    const isTitleTooLong = trimmedTitle.length > MAX_SECTION_TITLE_LENGTH
    if (isTitleTooShort) {
      globalAlertDialogStore.openAlertDialog({
        title: __('Section name is too short'),
        body: __('Section name must be at least %{min} characters long.', { min: MIN_SECTION_TITLE_LENGTH }),
        closeButtonText: __('OK'),
        shouldFadeIn: true,
      })
      return false
    }

    if (isTitleTooLong) {
      globalAlertDialogStore.openAlertDialog({
        title: __('Section name is too long'),
        body: __('Section name must be at most %{max} characters long.', { max: MAX_SECTION_TITLE_LENGTH }),
        closeButtonText: __('OK'),
        shouldFadeIn: true,
      })
      return false
    }

    // Update local state first so it appears to be updated immediately
    const section = sectionEntities.value[sectionId]
    if (section.title !== trimmedTitle) {
      updateSection({ ...section, title: trimmedTitle })
    }

    // Now update the server. Can be done asynchronously.
    // Still an asynchronous block, just that the order of execution within the block is guaranteed.
    // We want to ensure that the section title is updated on the server before we attempt to translate it.
    void (async () => {
      const { WallSectionApi } = await import('@@/surface/api/wall_section')
      await WallSectionApi.patch(section.id, { title: trimmedTitle })
      await useNativeAppStore().postSurfaceState()
      if (surfaceTranslationsStore.isViewingTranslatedPadlet) {
        void surfaceTranslationsStore.translateSectionEntity(section.id)
      }
    })()

    return true
  }

  function startRenamingSection(sectionId: Id): void {
    sectionBeingRenamedId.value = sectionId
  }

  function stopRenamingSection(): void {
    sectionBeingRenamedId.value = null
  }

  /*
   * DELETE ACTIONS
   */

  async function deleteSection(
    sectionId: Id,
    options: DeleteSectionOptions = { showSnackbar: true, trackHistory: true },
  ): Promise<void> {
    const { showSnackbar = true, trackHistory = true } = options

    try {
      const existingSection = sectionEntities.value[sectionId]
      if (existingSection == null) return

      let undoRedoData: SectionActionHistoryData[] = []

      if (isAppUsing('undoRedoDeleteSectionActions') && trackHistory) {
        undoRedoData = [
          extractSectionDataForUndoRedo(existingSection, surfacePostsStore.postsBySectionId[sectionId] ?? []),
        ]
      }

      const { WallSectionApi } = await import('@@/surface/api/wall_section')
      await WallSectionApi.delete(existingSection, {
        query: { soft_delete: isAppUsing('undoRedoDeleteSectionActions') },
      })
      removeSection(existingSection)
      if (showSnackbar && trackHistory) {
        if (isAppUsing('undoRedoDeleteSectionActions')) {
          const actionHistoryId = recordActionHistory(
            surfaceStore.wallHashid,
            ActionHistoryType.DELETE_SECTIONS,
            undoRedoData,
          )
          setDeletedSnackbar(ActionHistoryType.DELETE_SECTIONS, actionHistoryId, undoRedoData)
        } else {
          globalSnackbarStore.setSnackbar({
            notificationType: SnackbarNotificationType.success,
            message: __('Section deleted'),
          })
        }
      }

      if (sectionId === defaultSectionId.value) {
        // if deleted section is default, switch default to the first section to match new default in backend
        defaultSectionId.value = sortedSectionIds.value[0] ?? null
      }
      useSurfaceDraftsStore().reassignSections({
        oldSectionId: sectionId,
        newSectionId: defaultSectionId.value as number,
      })
      void trackEvent('Surface', 'Deleted section', null, null, { sectionId })
      void nativeAppStore.postSurfaceState()
    } catch (e) {
      globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'deleteSection' })
    }
  }

  function getPostsToMoveFromSection(sectionId: Id): Post[] {
    return surfacePostsStore.postsBySectionId[sectionId] ?? []
  }

  function showCannotDeleteLastSectionAlert(): void {
    globalAlertDialogStore.openAlertDialog({
      title: __('Section cannot be deleted'),
      body: __('At least one section is required. Please create a new section if you want to delete this one.'),
      closeButtonText: __('OK'),
      shouldFadeIn: true,
    })
  }

  async function showDialogForDeletingSectionWithNoPosts(sectionId: Id): Promise<void> {
    await confirmationDialogStore.openConfirmationDialog({
      iconSrc: ALERT_ICON.iconSrc,
      title: __('Are you sure you want to delete this section?'),
      body: !isAppUsing('undoRedoDeleteSectionActions') ? __('This cannot be undone!') : undefined,
      confirmButtonText: __('Delete'),
      cancelButtonText: __('Cancel'),
      buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
      afterConfirmActions: [
        async () => {
          await deleteSection(sectionId)
        },
      ],
    })
  }

  async function moveWishesAndDeleteSection(sectionId: Id, targetSectionId: Id, postsToMove: Post[]): Promise<void> {
    const sourceSection = getSectionById(sectionId)
    const targetSection = getSectionById(targetSectionId)

    try {
      if (sourceSection == null || targetSection == null) {
        throw new Error('Source or target section not found')
      }

      const { WallSectionApi } = await import('@@/surface/api/wall_section')
      const { moved_wishes_count, deleted } = await WallSectionApi.moveWishes(sectionId, targetSectionId, true)

      if (!deleted) {
        throw new Error('Failed to delete section')
      }

      // Realtime events will update UI only if they are triggered by other users, so for the current user,
      // We need to update the local state of the section and posts manually
      await Promise.all(
        postsToMove.map((post) =>
          surfacePostsStore.updatePostSection({ postCid: post.cid, sectionId: targetSectionId }),
        ),
      )
      removeSectionRemote(sourceSection)

      globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.success,
        message: __('%{movedWishesCount} posts in "%{sourceSection}" have been moved to "%{targetSection}"', {
          movedWishesCount: moved_wishes_count,
          sourceSection: sourceSection.title,
          targetSection: targetSection.title,
        }),
      })
      trackEvent('Surface', 'Deleted section and moved posts', null, null, {
        sourceSectionId: sectionId,
        targetSectionId,
      })
    } catch (error) {
      globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.error,
        message: __('Failed to delete section and move posts. Please try again.'),
      })
      captureException(error)
    }
  }

  async function showDialogForDeletingSectionWithPosts(sectionId: Id, postsToMove: Post[]): Promise<void> {
    await confirmationDialogStore.openConfirmationDialog({
      title: __('Are you sure you want to delete this section?'),
      body: !isAppUsing('undoRedoDeleteSectionActions')
        ? __('Deleting the section with its posts <b> cannot be undone.</b>')
        : undefined,
      iconSrc: ALERT_ICON.iconSrc,
      confirmButtonText: __('Delete posts and section'),
      discardButtonText: __('Delete section and move posts'),
      cancelButtonText: __('Cancel'),
      buttonScheme: OzConfirmationDialogBoxButtonScheme.RestartDiscardCancel,
      forceFullWidthButtons: true,
      afterConfirmActions: [async () => await deleteSection(sectionId)],
      afterDiscardActions: [
        async () => {
          const groupPickerStore = useSurfaceGroupPickerStore()
          const result = await groupPickerStore.show(__('Pick a section to move posts to'), {
            excludedGroupIds: [sectionId],
          })

          if (typeof result?.groupId === 'number') {
            await moveWishesAndDeleteSection(sectionId, result.groupId, postsToMove)
          }
        },
      ],
    })
  }

  async function askToDeleteSection(sectionId: Id | null): Promise<void> {
    if (sectionId == null) return

    if (sortedSections.value.length === 1) {
      showCannotDeleteLastSectionAlert()
      return
    }

    const postsToMove = getPostsToMoveFromSection(sectionId)

    if (postsToMove.length === 0) {
      trackEvent('Surface', 'Deleting section with no posts', null, null, { sectionId })
      await showDialogForDeletingSectionWithNoPosts(sectionId)
    } else {
      trackEvent('Surface', 'Deleting section with posts', null, null, { sectionId })
      await showDialogForDeletingSectionWithPosts(sectionId, postsToMove)
    }
  }

  /*
   * MOVE ACTIONS
   */

  function sortSection(payload: { sectionId: Id; sortIndex: number; gapSize: number }): void {
    const { sectionId, sortIndex, gapSize } = payload
    const sectionToChange = getSectionById(sectionId)
    if (sectionToChange == null) {
      throw new Error("Section doesn't exist")
    }
    const isSameIndex = sectionToChange.sort_index === sortIndex
    const isStuck = gapSize <= 4
    if (isSameIndex && !isStuck) return

    const updatedSection = { ...sectionToChange, sort_index: sortIndex }
    updateSection(updatedSection)
    void import('@@/surface/api/wall_section').then(({ WallSectionApi }) => {
      void WallSectionApi.patch(updatedSection.id, { sort_index: sortIndex }, gapSize)
      void useNativeAppStore().postSurfaceState()
    })
  }

  function moveSectionBefore(sectionId: Id): void {
    const index = sortedSectionIds.value.indexOf(sectionId)

    // No-op if already the first section
    if (index <= 0) return

    const section = sortedSections.value[index]
    let sortIndex = section.sort_index
    let gapSize: number | null = null

    if (index === 1) {
      // If is second-to-first section, use top sort index
      // to become the new first section
      sortIndex = sectionSorter.getTopSortIndex()
      const currentFirstSection = sortedSections.value[0]
      gapSize = currentFirstSection.sort_index - sortIndex
    } else {
      const newBeforeSection = sortedSections.value[index - 2]
      const newAfterSection = sortedSections.value[index - 1]
      sortIndex = (newAfterSection.sort_index + newBeforeSection.sort_index) / 2
      gapSize = (newAfterSection.sort_index - newBeforeSection.sort_index) / 2
    }

    sortSection({ sectionId, sortIndex, gapSize })
  }

  function moveSectionAfter(sectionId: Id): void {
    const index = sortedSectionIds.value.indexOf(sectionId)

    // No-op if already the last section
    if (index === -1 || index >= sortedSectionIds.value.length - 1) return

    const section = sortedSections.value[index]
    let sortIndex = section.sort_index
    let gapSize: number | null = null

    if (index === sortedSections.value.length - 2) {
      // If is second-to-last section, use bottom sort index
      // to become the new last section
      sortIndex = sectionSorter.getBottomSortIndex()
      const currentLastSection = sortedSections.value[sortedSections.value.length - 1]
      gapSize = sortIndex - currentLastSection.sort_index
    } else {
      const newBeforeSection = sortedSections.value[index + 1]
      const newAfterSection = sortedSections.value[index + 2]
      sortIndex = (newAfterSection.sort_index + newBeforeSection.sort_index) / 2
      gapSize = (newAfterSection.sort_index - newBeforeSection.sort_index) / 2
    }

    sortSection({ sectionId, sortIndex, gapSize })
  }

  /*
   * REMOTE ACTION
   */
  function insertSectionRemote(section: Section): void {
    insertSection(section)
    void nativeAppStore.postSurfaceState()
    if (surfaceTranslationsStore.isViewingTranslatedPadlet) {
      void surfaceTranslationsStore.translateSectionEntity(section.id)
    }
  }

  function updateSectionRemote(section: Section): void {
    updateSection(section)
    void nativeAppStore.postSurfaceState()
    if (surfaceTranslationsStore.isViewingTranslatedPadlet) {
      void surfaceTranslationsStore.translateSectionEntity(section.id)
    }
  }

  function removeSectionRemote(section: Section): void {
    removeSection(section)
    if (surfaceTranslationsStore.isViewingTranslatedPadlet) {
      void surfaceTranslationsStore.deleteSectionFromTranslatedWall(section.id)
    }
    void nativeAppStore.postSurfaceState()
  }

  async function restoreSection(sectionId: Id): Promise<Section> {
    const { WallSectionApi } = await import('@@/surface/api/wall_section')
    const restoredSectionData = await WallSectionApi.restore(sectionId)
    const section = (restoredSectionData.data as JsonAPIResource<Section>).attributes
    insertSection(section)
    updateMostRecentlyTouchedSection(section.id)
    void nativeAppStore.postSurfaceState()
    void surfaceShareLinksStore.fetchSectionBreakoutLinks()
    return section
  }

  // #region Undo/Redo
  async function undoDeleteSections(
    sectionActionHistory: SectionActionHistoryData[],
  ): Promise<SectionActionHistoryData[]> {
    if (!isAppUsing('undoRedoDeleteSectionActions')) return sectionActionHistory

    return await Promise.all(
      sectionActionHistory.map(async (sectionData) => {
        const section = await restoreSection(sectionData.sectionAttributes.id)

        const posts = await surfacePostsStore.undoDeletePosts(
          sectionData.posts.map((post) => ({
            ...post,
            wall_section_id: section.id,
            wallSectionId: section.id,
            wall_section_hashid: section.hashid,
          })),
        )
        return {
          ...sectionData,
          sectionAttributes: {
            id: section.id,
            title: section.title,
            sort_index: section.sort_index,
            color: section.color,
          },
          posts,
        }
      }),
    )
  }

  async function redoDeleteSections(
    sectionActionHistory: SectionActionHistoryData[],
  ): Promise<SectionActionHistoryData[]> {
    if (!isAppUsing('undoRedoDeleteSectionActions')) return sectionActionHistory

    return await Promise.all(
      sectionActionHistory.map(async (sectionData) => {
        await deleteSection(sectionData.sectionAttributes.id, {
          showSnackbar: false,
          trackHistory: false,
        })
        return sectionData
      }),
    )
  }
  // #endregion

  return {
    // state
    sectionEntities,
    sectionRecentlyCreatedId,
    sectionBeingRenamedId,
    mostRecentlyTouchedSectionId,
    sectionUnderActionId,
    sectionUnderActionRightClickCoordinates,
    wallHasSections,

    // getters
    sectionUnderAction,
    defaultSectionId,
    sectionIds,
    sortedSections,
    rawSortedSections,
    sortedSectionIds,
    usableSections,
    numOfSections,
    canEditSectionNameInline,
    xSectionRenameModal,
    xSectionSpecificButtons,
    hasFetchedSections,
    assignedWallSectionId,
    isSectionBeingEdited,

    // getter functions
    getSectionById,
    getRawSectionById,
    isSectionEmpty,
    hasPinnedPosts,
    hasUnpinnedPosts,

    // actions
    hideSectionActionMenu,
    updateSectionEntities,
    fetchSections,
    insertSectionRemote,
    updateSectionRemote,
    removeSectionRemote,
    updateMostRecentlyTouchedSection,
    setDefaultSectionAndRecentlyTouchedSection,
    changeDefaultSection,
    createSectionWithName,
    createSectionWithDefaultName,
    createSectionBefore,
    createSectionAfter,
    editSectionTitle,
    startRenamingSection,
    stopRenamingSection,
    deleteSection,
    askToDeleteSection,
    restoreSection,
    sortSection,
    moveSectionBefore,
    moveSectionAfter,
    undoDeleteSections,
    redoDeleteSections,
  }
})
